import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { theme } from '../styles/theme';
import { FiX } from 'react-icons/fi';
import { supabase } from '../../../services/supabaseClient';

// Evento global pra abrir o modal de qualquer lugar
export function openContactModal() {
  window.dispatchEvent(new CustomEvent('open-contact-modal'));
}

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  animation: ${fadeIn} 200ms ease-out;
`;

const Modal = styled.div`
  background: #FFFFFF;
  border-radius: 12px;
  width: 100%;
  max-width: 440px;
  padding: 40px 36px;
  position: relative;
  animation: ${slideUp} 300ms ease-out;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 32px 24px;
    max-width: 100%;
  }
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  cursor: pointer;
  color: ${theme.colors.textMuted};
  font-size: 20px;
  padding: 4px;
  display: flex;
  transition: color ${theme.transitions.fast};

  &:hover {
    color: ${theme.colors.textPrimary};
  }
`;

const Title = styled.h2`
  font-family: ${theme.fonts.serif};
  font-size: 28px;
  font-weight: 300;
  letter-spacing: -0.02em;
  color: ${theme.colors.textPrimary};
  margin: 0 0 8px 0;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: ${theme.colors.textSecondary};
  margin: 0 0 32px 0;
  line-height: 1.5;
`;

const Field = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${theme.colors.textSecondary};
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 0;
  border: none;
  border-bottom: 1px solid ${theme.colors.border};
  font-family: ${theme.fonts.sans};
  font-size: 15px;
  color: ${theme.colors.textPrimary};
  background: transparent;
  transition: border-color ${theme.transitions.fast};
  outline: none;

  &::placeholder {
    color: ${theme.colors.textMuted};
  }

  &:focus {
    border-bottom-color: ${theme.colors.textPrimary};
  }
`;

const SubmitBtn = styled.button`
  width: 100%;
  padding: 14px;
  margin-top: 12px;
  background: ${theme.colors.bgDark};
  color: #FFFFFF;
  border: none;
  border-radius: 4px;
  font-family: ${theme.fonts.sans};
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  cursor: pointer;
  transition: background ${theme.transitions.fast};

  &:hover {
    background: #1A3050;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SuccessMsg = styled.div`
  text-align: center;
  padding: 24px 0;

  h3 {
    font-family: ${theme.fonts.serif};
    font-size: 24px;
    font-weight: 300;
    color: ${theme.colors.textPrimary};
    margin: 0 0 8px 0;
  }

  p {
    font-size: 14px;
    color: ${theme.colors.textSecondary};
    margin: 0;
  }
`;

export default function ContactModal() {
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ nome: '', telefone: '', email: '' });
  const [leadPhone, setLeadPhone] = useState('');

  const handleOpen = useCallback(() => {
    setOpen(true);
    setSent(false);
    setForm({ nome: '', telefone: '', email: '' });
  }, []);

  useEffect(() => {
    window.addEventListener('open-contact-modal', handleOpen);
    return () => window.removeEventListener('open-contact-modal', handleOpen);
  }, [handleOpen]);

  useEffect(() => {
    supabase.from('mf_config').select('telefone_encaminhamento_leads').eq('id', 1).single()
      .then(({ data }) => {
        if (data?.telefone_encaminhamento_leads) setLeadPhone(data.telefone_encaminhamento_leads);
      });
  }, []);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const close = () => setOpen(false);

  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      // Salva lead no Supabase
      const { data: lead, error } = await supabase
        .from('mf_leads_site')
        .insert({ nome: form.nome.trim(), telefone: form.telefone.trim(), email: form.email.trim() || null })
        .select('id')
        .single();

      if (error) {
        console.error('Erro ao salvar lead:', error);
        setSending(false);
        return;
      }

      // Dispara notificacao via Trigger.dev
      fetch('/api/mf-lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lead_id: lead.id }),
      }).catch(() => {}); // fire-and-forget

      // Abre WhatsApp pro cliente continuar a conversa se quiser
      if (leadPhone) {
        const msg = encodeURIComponent(
          `Olá! Me chamo ${form.nome.trim()} e acabei de deixar meus dados no site.`
        );
        window.open(`https://wa.me/${leadPhone}?text=${msg}`, '_blank', 'noopener,noreferrer');
      }

      setSent(true);
    } catch (err) {
      console.error('Erro no submit:', err);
    }
    setSending(false);
  };

  const handleChange = (field) => (e) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  if (!open) return null;

  const canSubmit = form.nome.trim() && form.telefone.trim();

  return (
    <Overlay onClick={close}>
      <Modal onClick={e => e.stopPropagation()}>
        <CloseBtn onClick={close}><FiX /></CloseBtn>

        {sent ? (
          <SuccessMsg>
            <h3>Mensagem enviada</h3>
            <p>Entraremos em contato em breve.</p>
          </SuccessMsg>
        ) : (
          <>
            <Title>Fale conosco</Title>
            <Subtitle>Preencha seus dados e entraremos em contato.</Subtitle>

            <form onSubmit={handleSubmit}>
              <Field>
                <Label>Nome</Label>
                <Input
                  type="text"
                  placeholder="Seu nome"
                  value={form.nome}
                  onChange={handleChange('nome')}
                  autoFocus
                />
              </Field>

              <Field>
                <Label>Telefone</Label>
                <Input
                  type="tel"
                  placeholder="(11) 99999-9999"
                  value={form.telefone}
                  onChange={handleChange('telefone')}
                />
              </Field>

              <Field>
                <Label>Email</Label>
                <Input
                  type="email"
                  placeholder="seu@email.com"
                  value={form.email}
                  onChange={handleChange('email')}
                />
              </Field>

              <SubmitBtn type="submit" disabled={!canSubmit || sending}>
                {sending ? 'Enviando...' : 'Enviar'}
              </SubmitBtn>
            </form>
          </>
        )}
      </Modal>
    </Overlay>
  );
}
